import type { ReactNode } from "react";
import { AVATAR_PRESETS, type AvatarPreset } from "@kandoo/shared";

/** Human labels for the picker, in the shared preset order. */
export const AVATAR_PRESET_LABELS: Record<AvatarPreset, string> = {
  duck: "Duck",
  cow: "Cow",
  pig: "Pig",
  frog: "Frog",
  goose: "Goose",
  cheese: "Cheese",
  space: "Space",
  robot: "Robot",
  cat: "Cat",
  fox: "Fox",
  owl: "Owl",
  mushroom: "Mushroom",
  turtle: "Turtle",
  donut: "Donut",
  sushi: "Sushi",
  avocado: "Avocado",
  strawberry: "Strawberry",
  coffee: "Coffee",
  boba: "Boba",
  beer: "Beer",
  earth: "Earth",
  mars: "Mars",
  moon: "Moon",
};

/**
 * Picker layout: presets grouped into labelled sections. Every key in
 * AVATAR_PRESETS must appear in exactly one group.
 */
export const AVATAR_PRESET_GROUPS: { label: string; keys: AvatarPreset[] }[] = [
  {
    label: "Animals",
    keys: ["duck", "cow", "pig", "frog", "goose", "cat", "fox", "owl", "turtle"],
  },
  {
    label: "Food",
    keys: ["cheese", "donut", "sushi", "avocado", "strawberry"],
  },
  { label: "Drinks", keys: ["coffee", "boba", "beer"] },
  { label: "Space", keys: ["space", "earth", "mars", "moon"] },
  { label: "Other", keys: ["robot", "mushroom"] },
];

/**
 * Flat SVG art for each preset, drawn on a 24×24 canvas whose background disc
 * fills the round avatar frame. Kept deliberately simple — a handful of shapes
 * each — so they stay legible down to 24px.
 */
const ART: Record<AvatarPreset, ReactNode> = {
  duck: (
    <>
      <circle cx="12" cy="12" r="12" fill="#bde0fe" />
      <circle cx="10.5" cy="12" r="6" fill="#ffd23f" />
      <circle cx="12" cy="10" r="1" fill="#1d3557" />
      <path d="M15 11.4h4.2a1.1 1.1 0 010 2.2H15z" fill="#fb8500" />
    </>
  ),
  cow: (
    <>
      <circle cx="12" cy="12" r="12" fill="#a7c957" />
      <circle cx="5.5" cy="9" r="2.4" fill="#f8f9fa" />
      <circle cx="18.5" cy="9" r="2.4" fill="#f8f9fa" />
      <circle cx="12" cy="12" r="7" fill="#f8f9fa" />
      <path d="M6.5 8.5a3 3 0 013 1l-1 3a3 3 0 01-3.2-1z" fill="#22223b" />
      <circle cx="15.6" cy="9.6" r="1.9" fill="#22223b" />
      <circle cx="9.6" cy="11.6" r="1" fill="#22223b" />
      <circle cx="14.4" cy="11.6" r="1" fill="#22223b" />
      <ellipse cx="12" cy="16" rx="4" ry="3" fill="#ffc8dd" />
      <circle cx="10.6" cy="16" r="0.7" fill="#c9184a" />
      <circle cx="13.4" cy="16" r="0.7" fill="#c9184a" />
    </>
  ),
  pig: (
    <>
      <circle cx="12" cy="12" r="12" fill="#90caf9" />
      <path d="M6 6l3.2 3-2 3L4 8z" fill="#ff8fab" />
      <path d="M18 6l-3.2 3 2 3L20 8z" fill="#ff8fab" />
      <circle cx="12" cy="13" r="7" fill="#ffafcc" />
      <circle cx="9.6" cy="11.6" r="1" fill="#6d213c" />
      <circle cx="14.4" cy="11.6" r="1" fill="#6d213c" />
      <ellipse cx="12" cy="15.6" rx="3.2" ry="2.4" fill="#ff8fab" />
      <circle cx="11" cy="15.6" r="0.6" fill="#6d213c" />
      <circle cx="13" cy="15.6" r="0.6" fill="#6d213c" />
    </>
  ),
  frog: (
    <>
      <circle cx="12" cy="12" r="12" fill="#b7e4c7" />
      <circle cx="12" cy="14" r="7" fill="#52b788" />
      <circle cx="8" cy="8.5" r="3" fill="#52b788" />
      <circle cx="16" cy="8.5" r="3" fill="#52b788" />
      <circle cx="8" cy="8.5" r="1.4" fill="#fff" />
      <circle cx="16" cy="8.5" r="1.4" fill="#fff" />
      <circle cx="8" cy="8.5" r="0.7" fill="#1b4332" />
      <circle cx="16" cy="8.5" r="0.7" fill="#1b4332" />
      <path
        d="M8.5 14.5a3.5 2.5 0 007 0"
        fill="none"
        stroke="#1b4332"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </>
  ),
  goose: (
    <>
      <circle cx="12" cy="12" r="12" fill="#cae9ff" />
      <path
        d="M10 20c-1-4 0-9 3-11"
        fill="none"
        stroke="#f8f9fa"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <circle cx="13" cy="8" r="4" fill="#f8f9fa" />
      <circle cx="13.6" cy="7" r="0.9" fill="#1d3557" />
      <path d="M16 7.4h3.5a1 1 0 010 2H16z" fill="#fb8500" />
    </>
  ),
  cheese: (
    <>
      <circle cx="12" cy="12" r="12" fill="#eaddca" />
      <path
        d="M3 14 L19 8 a2 2 0 012 2 v3 a2 2 0 01-1.4 1.9 L6 18 a3 3 0 01-3-4z"
        fill="#ffca3a"
      />
      <path d="M19 8a2 2 0 012 2l-4 1z" fill="#f4a300" />
      <circle cx="9" cy="14" r="1.1" fill="#e8a600" />
      <circle cx="14" cy="12.6" r="0.9" fill="#e8a600" />
      <circle cx="12" cy="15.6" r="0.7" fill="#e8a600" />
    </>
  ),
  space: (
    <>
      <circle cx="12" cy="12" r="12" fill="#22223b" />
      <circle cx="6" cy="6" r="0.7" fill="#fff" />
      <circle cx="18.5" cy="17" r="0.6" fill="#fff" />
      <circle cx="19" cy="7" r="0.5" fill="#fff" />
      <ellipse
        cx="12"
        cy="12"
        rx="8"
        ry="2.6"
        fill="none"
        stroke="#e9c46a"
        strokeWidth="1.3"
        transform="rotate(-20 12 12)"
      />
      <circle cx="12" cy="12" r="4.5" fill="#c77dff" />
    </>
  ),
  robot: (
    <>
      <circle cx="12" cy="12" r="12" fill="#adb5bd" />
      <line x1="12" y1="4.2" x2="12" y2="7" stroke="#495057" strokeWidth="1.2" />
      <circle cx="12" cy="4" r="1.2" fill="#ef476f" />
      <rect x="6" y="7" width="12" height="10" rx="2.5" fill="#495057" />
      <circle cx="9.5" cy="11" r="1.6" fill="#4cc9f0" />
      <circle cx="14.5" cy="11" r="1.6" fill="#4cc9f0" />
      <rect x="9" y="14" width="6" height="1.6" rx="0.8" fill="#4cc9f0" />
    </>
  ),
  cat: (
    <>
      <circle cx="12" cy="12" r="12" fill="#ffd6a5" />
      <path d="M6 6 L8.5 11 L4 10 Z" fill="#f4a261" />
      <path d="M18 6 L15.5 11 L20 10 Z" fill="#f4a261" />
      <circle cx="12" cy="13" r="7" fill="#f4a261" />
      <circle cx="9.5" cy="12" r="1" fill="#3a2618" />
      <circle cx="14.5" cy="12" r="1" fill="#3a2618" />
      <path d="M11 14.6h2l-1 1.2z" fill="#e5989b" />
      <g stroke="#3a2618" strokeWidth="0.6" strokeLinecap="round">
        <line x1="5" y1="14" x2="9" y2="14.5" />
        <line x1="5" y1="16" x2="9" y2="15.5" />
        <line x1="19" y1="14" x2="15" y2="14.5" />
        <line x1="19" y1="16" x2="15" y2="15.5" />
      </g>
    </>
  ),
  fox: (
    <>
      <circle cx="12" cy="12" r="12" fill="#ffe8d6" />
      <path d="M5 5 L9.5 10 L4.5 10.5 Z" fill="#e76f51" />
      <path d="M19 5 L14.5 10 L19.5 10.5 Z" fill="#e76f51" />
      <path
        d="M12 6a7 6 0 016.5 7c0 2.5-3 4.5-6.5 4.5S5.5 15.5 5.5 13A7 6 0 0112 6z"
        fill="#e76f51"
      />
      <path d="M12 11 L16.5 12.2 L12 18 L7.5 12.2 Z" fill="#fff" />
      <circle cx="9.4" cy="11.6" r="1" fill="#3a2618" />
      <circle cx="14.6" cy="11.6" r="1" fill="#3a2618" />
      <circle cx="12" cy="15" r="1.1" fill="#3a2618" />
    </>
  ),
  owl: (
    <>
      <circle cx="12" cy="12" r="12" fill="#cdb4db" />
      <path d="M7 5l1.6 3.2-3.2.2z" fill="#9c6644" />
      <path d="M17 5l-1.6 3.2 3.2.2z" fill="#9c6644" />
      <ellipse cx="12" cy="13" rx="7" ry="7.5" fill="#9c6644" />
      <ellipse cx="12" cy="15" rx="4" ry="4.5" fill="#c8a27c" />
      <circle cx="9" cy="11" r="2.6" fill="#fff" />
      <circle cx="15" cy="11" r="2.6" fill="#fff" />
      <circle cx="9" cy="11" r="1.1" fill="#3a2618" />
      <circle cx="15" cy="11" r="1.1" fill="#3a2618" />
      <path d="M11 13h2l-1 1.6z" fill="#f4a300" />
    </>
  ),
  mushroom: (
    <>
      <circle cx="12" cy="12" r="12" fill="#d8f3dc" />
      <path d="M9.5 13h5v4a2.5 2.5 0 01-5 0z" fill="#f2e8cf" />
      <path d="M4 13a8 6 0 0116 0z" fill="#e63946" />
      <circle cx="9" cy="10" r="1.1" fill="#fff" />
      <circle cx="14" cy="9.5" r="0.9" fill="#fff" />
      <circle cx="16.4" cy="11.4" r="0.8" fill="#fff" />
      <circle cx="6.6" cy="11.4" r="0.7" fill="#fff" />
      <circle cx="11" cy="15" r="0.6" fill="#3a2618" />
      <circle cx="13" cy="15" r="0.6" fill="#3a2618" />
    </>
  ),
  turtle: (
    <>
      <circle cx="12" cy="12" r="12" fill="#a8dadc" />
      <ellipse cx="7.5" cy="16.3" rx="1.7" ry="1.1" fill="#6a994e" />
      <ellipse cx="14.5" cy="16.3" rx="1.7" ry="1.1" fill="#6a994e" />
      <circle cx="18.3" cy="13" r="2.2" fill="#6a994e" />
      <circle cx="19.1" cy="12.5" r="0.5" fill="#1b4332" />
      <path d="M4.5 15.5a6.5 5 0 0113 0z" fill="#588157" />
      <path
        d="M11 10.6v4.9M7.8 12l1.3 3.5M14.2 12l-1.3 3.5"
        stroke="#3a5a40"
        strokeWidth="0.8"
        fill="none"
      />
    </>
  ),
  donut: (
    <>
      <circle cx="12" cy="12" r="12" fill="#cdeafc" />
      <circle cx="12" cy="12" r="8.5" fill="#e8a06a" />
      <circle cx="12" cy="12" r="7" fill="#ff8fab" />
      <circle cx="12" cy="12" r="3" fill="#cdeafc" />
      <g strokeWidth="1.1" strokeLinecap="round">
        <line x1="8.6" y1="8" x2="9.4" y2="8.9" stroke="#fefae0" />
        <line x1="14" y1="8.2" x2="15" y2="8.8" stroke="#a0c4ff" />
        <line x1="12" y1="6.4" x2="12.9" y2="6.9" stroke="#ffca3a" />
        <line x1="7.4" y1="13.6" x2="8.4" y2="14" stroke="#90e0ef" />
        <line x1="15" y1="14.6" x2="15.9" y2="15.2" stroke="#b5e48c" />
        <line x1="10.5" y1="16.6" x2="11.5" y2="16.9" stroke="#ffca3a" />
      </g>
    </>
  ),
  sushi: (
    <>
      <circle cx="12" cy="12" r="12" fill="#f6bd60" />
      <rect x="4" y="12" width="16" height="7" rx="3.5" fill="#f8f9fa" />
      <rect x="4" y="7.5" width="16" height="5.5" rx="2.5" fill="#f4795b" />
      <path d="M5 9.5h14M5 11h14" stroke="#f9a26c" strokeWidth="0.7" />
      <rect x="10.3" y="7.5" width="3.4" height="11.5" fill="#2f3e46" />
    </>
  ),
  avocado: (
    <>
      <circle cx="12" cy="12" r="12" fill="#e9f5db" />
      <ellipse cx="12" cy="12" rx="6.5" ry="8" fill="#386641" />
      <ellipse cx="12" cy="12" rx="4.7" ry="6.2" fill="#c7d59f" />
      <circle cx="12" cy="14" r="2.7" fill="#8a5a44" />
    </>
  ),
  strawberry: (
    <>
      <circle cx="12" cy="12" r="12" fill="#ffe5ec" />
      <path
        d="M12 7C7.5 7 5.5 10.5 6.5 13.5 7.3 16.8 10 19 12 20c2-1 4.7-3.2 5.5-6.5C18.5 10.5 16.5 7 12 7z"
        fill="#e63946"
      />
      <path d="M8.5 8L12 5.5 15.5 8c-1.5 1-5.5 1-7 0z" fill="#52b788" />
      <g fill="#ffe066">
        <circle cx="9.5" cy="11" r="0.5" />
        <circle cx="12" cy="10.5" r="0.5" />
        <circle cx="14.5" cy="11" r="0.5" />
        <circle cx="10.5" cy="13.5" r="0.5" />
        <circle cx="13.5" cy="13.5" r="0.5" />
        <circle cx="12" cy="15.8" r="0.5" />
      </g>
    </>
  ),
  coffee: (
    <>
      <circle cx="12" cy="12" r="12" fill="#ede0d4" />
      <path
        d="M9 4.5q1.2 1 0 2.2M12 4q1.2 1 0 2.2"
        fill="none"
        stroke="#c9bdb0"
        strokeWidth="0.9"
        strokeLinecap="round"
      />
      <rect x="5" y="9" width="11" height="9.5" rx="2" fill="#e76f51" />
      <path
        d="M16 11.5a2.6 2.6 0 010 5"
        fill="none"
        stroke="#e76f51"
        strokeWidth="1.8"
      />
      <ellipse cx="10.5" cy="9.4" rx="5" ry="1.4" fill="#4a2c2a" />
    </>
  ),
  boba: (
    <>
      <circle cx="12" cy="12" r="12" fill="#cdeafc" />
      <path d="M7 8h10l-1 11H8z" fill="#e4c59e" />
      <rect x="6.3" y="6.3" width="11.4" height="2.2" rx="1" fill="#f1faee" />
      <rect
        x="13"
        y="4"
        width="1.8"
        height="13"
        rx="0.9"
        transform="rotate(12 13.9 10)"
        fill="#ef476f"
      />
      <g fill="#3a2618">
        <circle cx="9" cy="17" r="1" />
        <circle cx="11.3" cy="17.4" r="1" />
        <circle cx="13.6" cy="17" r="1" />
        <circle cx="10.2" cy="15.4" r="1" />
        <circle cx="12.5" cy="15.4" r="1" />
      </g>
    </>
  ),
  beer: (
    <>
      <circle cx="12" cy="12" r="12" fill="#a9d6e5" />
      <rect x="6" y="8.5" width="9" height="10.5" rx="1.5" fill="#f6ae2d" />
      <rect x="6" y="6.5" width="9" height="3" rx="1.5" fill="#fffdf5" />
      <path
        d="M15 11a2.8 2.8 0 010 5.5"
        fill="none"
        stroke="#f6ae2d"
        strokeWidth="1.8"
      />
      <g fill="#fde68a">
        <circle cx="8.5" cy="12" r="0.7" />
        <circle cx="11" cy="14" r="0.7" />
        <circle cx="9.5" cy="16" r="0.6" />
        <circle cx="12.5" cy="11.5" r="0.6" />
      </g>
    </>
  ),
  earth: (
    <>
      <circle cx="12" cy="12" r="12" fill="#03071e" />
      <circle cx="19" cy="6" r="0.6" fill="#fff" />
      <circle cx="5" cy="18" r="0.5" fill="#fff" />
      <circle cx="12" cy="12" r="8" fill="#219ebc" />
      <path d="M7 9q3-1 5 1-1 2-3 2-2 0-2-3z" fill="#588157" />
      <path d="M13 14q3-1 4 1-1 2-3 1.5-1-.5-1-2.5z" fill="#588157" />
      <path d="M9 15q2 0 2.5 1.5-1.5 1-3 0z" fill="#52b788" />
    </>
  ),
  mars: (
    <>
      <circle cx="12" cy="12" r="12" fill="#03071e" />
      <circle cx="6" cy="6" r="0.6" fill="#fff" />
      <circle cx="18" cy="17.5" r="0.5" fill="#fff" />
      <circle cx="12" cy="12" r="8" fill="#d1603d" />
      <circle cx="9.5" cy="10" r="1.6" fill="#b5432b" />
      <circle cx="14.5" cy="13.5" r="1.2" fill="#b5432b" />
      <circle cx="11" cy="15" r="0.9" fill="#b5432b" />
      <ellipse cx="12" cy="5.6" rx="3" ry="1.3" fill="#f1faee" />
    </>
  ),
  moon: (
    <>
      <circle cx="12" cy="12" r="12" fill="#03071e" />
      <circle cx="18.5" cy="6.5" r="0.7" fill="#fff" />
      <path
        d="M5.5 6l0.5 1.3 1.3 0.5-1.3 0.5-0.5 1.3-0.5-1.3-1.3-0.5 1.3-0.5z"
        fill="#fff"
      />
      <circle cx="12" cy="12" r="8" fill="#ced4da" />
      <circle cx="9" cy="9.5" r="1.6" fill="#adb5bd" />
      <circle cx="14.5" cy="11" r="1.1" fill="#adb5bd" />
      <circle cx="11" cy="14.5" r="1.3" fill="#adb5bd" />
      <circle cx="15" cy="15" r="0.8" fill="#adb5bd" />
    </>
  ),
};

const PRESET_PREFIX = "preset:";

/** True for a stored avatar value that names a known preset. */
export function isPresetValue(value: string | null | undefined): boolean {
  return !!value && parsePreset(value) !== null;
}

/** Extract the preset key from a `preset:<key>` value, or null if not a known preset. */
export function parsePreset(value: string | null | undefined): AvatarPreset | null {
  if (!value || !value.startsWith(PRESET_PREFIX)) return null;
  const key = value.slice(PRESET_PREFIX.length);
  return (AVATAR_PRESETS as readonly string[]).includes(key)
    ? (key as AvatarPreset)
    : null;
}

/** The stored value for a preset key (e.g. `preset:duck`). */
export function presetValue(preset: AvatarPreset): string {
  return `${PRESET_PREFIX}${preset}`;
}

/** Renders a single preset's SVG art, filling its container. */
export function PresetAvatar({
  preset,
  label,
}: {
  preset: AvatarPreset;
  label?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="100%"
      height="100%"
      role="img"
      aria-label={label ?? AVATAR_PRESET_LABELS[preset]}
    >
      {ART[preset]}
    </svg>
  );
}
